'use client';

import React from 'react';
import { styled } from '@linaria/react';

import { UserPreferencesContext } from '@/components/UserPreferencesProvider';
import RenderWhenOnscreen from '@/components/RenderWhenOnscreen';

import { ENVIRONMENT_WIDTH, ENVIRONMENT_HEIGHT } from './constants';
import EarthBackground from './EarthBackground';
import SpaceBackground from './SpaceBackground';

/**
 * `type` is one of:
 *   - `default` — grey background, uninteresting
 *   - `clear` - `default`, but with a transparent background
 *   - `earth` – blue skies, floating clouds
 *   - `space` - outer space
 *
 *
 * TODO: Does it maybe make sense to use portals for the foreground? That way,
 * everything related to clamps can sit in the same component...
 */

const Foreground = ({ colorMode, type, flipped }) => {
  if (type === 'space') {
    return (
      <ClampImg src="/images/a-friendly-introduction-to-spring-physics/top-clamp.png" />
    );
  }

  const showOutline = type === 'earth' ? false : colorMode === 'dark';

  return (
    <PlankWrapper data-flipped={String(flipped)}>
      <PlankAndClampImg src="/images/a-friendly-introduction-to-spring-physics/plank-and-clamp.png" />
      {showOutline && (
        <PlankAndClampOutline src="/images/a-friendly-introduction-to-spring-physics/plank-and-clamp-outline.png" />
      )}
    </PlankWrapper>
  );
};

const Background = ({ type, friction }) => {
  switch (type) {
    case 'earth': {
      return <EarthBackground friction={friction} />;
    }
    case 'space': {
      return <SpaceBackground />;
    }
    case 'clear': {
      return null;
    }
    default: {
      return <DefaultBackground />;
    }
  }
};

const Environment = ({ type, friction, flipped, children }) => {
  const { colorMode } = React.useContext(UserPreferencesContext);

  return (
    <RenderWhenOnscreen height={ENVIRONMENT_HEIGHT}>
      <Wrapper
        style={{
          width: ENVIRONMENT_WIDTH,
          height: ENVIRONMENT_HEIGHT,
        }}
      >
        <Foreground
          colorMode={colorMode}
          type={type}
          flipped={flipped}
        />
        <Children>{children}</Children>
        <Background type={type} friction={friction} />
      </Wrapper>
    </RenderWhenOnscreen>
  );
};

const Wrapper = styled.div`
  position: relative;
  touch-action: none;
  isolation: isolate;
`;

const PlankAndClampImg = styled.img`
  display: block;

  /*
    Magic number, derived from the parent image (the plank is 83% of the
    total width of the canvas).
    This ensures that the clamp bit is at the 50% position.
  */
  width: 83%;
`;
const ClampImg = styled.img`
  pointer-events: none;
  display: block;
  position: absolute;
  z-index: 4;
  top: 20px;
  left: 0;
  right: 0;
  width: 40px;
  margin: auto;
`;

const PlankWrapper = styled.div`
  position: absolute;
  z-index: 4;
  top: 8px;
  left: 0;
  right: 0;
  width: 100%;
  pointer-events: none;

  &[data-flipped='true'] {
    transform: rotateY(180deg);
  }
`;

const PlankAndClampOutline = styled(PlankAndClampImg)`
  position: absolute;
  top: 0;
  z-index: -1;
`;

const Children = styled.div`
  position: relative;
  z-index: 2;
  padding-top: 42px;
  pointer-events: none;
  height: 100%;
`;

const DefaultBackground = styled.div`
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background: var(--color-gray-100);
  border-radius: 10px;
`;

export default Environment;
